import { camelize } from "camelscore";
import { IndexedList } from "storefront/lib/IndexedList";
import memoize from "storefront/lib/Function/memoize";
import { Id } from "storefront/lib/Id";
import { Strata } from "storefront/Strata";
import { SizesByCategory } from "storefront/Size";
import { Categories } from "storefront/Categories";
import { Badge } from "storefront/Badge";
import { Country } from "storefront/Country";
import { SellerBadge } from "storefront/SellerBadge";
import { MeasurementType } from "storefront/MeasurementType";
import { Host } from "storefront/Contentful/Host";
import { Config as ContentfulConfig } from "storefront/Contentful/Config";
import { Name as ShippingRegionName } from "storefront/Shipping/Region";
import { Colors } from "storefront/Colors";
import { AlgoliaConfig, emptyAlgoliaConfig } from "./AlgoliaConfig";
import { SentryConfig, emptySentryConfig } from "./SentryConfig";

type Contentful$Space = {
  spaceId: Id;
  accessToken: string;
  entryIds?: IndexedList<string | IndexedList<string>>;
};

type Contentful = {
  host: Host;
  spaces: IndexedList<Contentful$Space>;
};

export type Environment = "production" | "development";

type GoogleAnalytics = {
  domain: string;
  trackingId: string;
};

export type ReasonOption =
  | "returning_scammer"
  | "charge_back"
  | "inauthentic"
  | "off_site"
  | "user_feedback"
  | "mod_review"
  | "other";

export type ScoreOption =
  | "high_risk"
  | "suspicious"
  | "unknown"
  | "low_risk"
  | "trustworthy";

type TrustOptions = {
  scores: Array<ScoreOption>;
  reasons: Array<ReasonOption>;
};

type Recaptcha = {
  siteKey?: string;
  siteKeyInvisible?: string;
};

type UserRefundReason = {
  key: string;
  value: string;
};

type UserRefundReasons = Array<UserRefundReason>;

type Style = {
  displayName: string;
  name: string;
};

// PublicConfigSerializer
export type PublicConfig = {
  algolia: AlgoliaConfig;
  appleSignInClientId: string;
  appleSignInRedirectUri: string;
  badges: Array<Badge>;
  appsflyerSmartBannerKey: string | null;
  branchKeyLive: string | null;
  categories: Categories;
  colors: Colors;
  contentful: Contentful;
  countries: Array<Country>;
  env: Environment;
  facebookAppId: number;
  googleAnalytics: GoogleAnalytics;
  googleOauthClientId: string;
  inBeta: boolean;
  internationalTaxable: Array<Country>;
  listingMaxPrice: number;
  listingMinPrice: number;
  listingGpfsMinPrice: number;
  locations: Array<ShippingRegionName>;
  measurementTypes: Array<MeasurementType>;
  recaptcha: Recaptcha;
  requiresPostalCode: Array<string>;
  sellformSuggestedDesignerIds: Array<number>;
  sellerBadges: Array<SellerBadge>;
  sizes: SizesByCategory;
  stratas: Array<Strata>;
  segmentWriteKey: string;
  stripePublishableKey: string;
  styles: Array<Style>;
  trustOptions: TrustOptions;
  unbrandedDesignerId: number;
  urls: IndexedList<string>;
  userRefundReasons: UserRefundReasons;
  vacationModeLimit: number;
  bundlesDomain: string;
  riskifiedShopDomain: string;
  oneTrustDomainScript: string;
  sentry: SentryConfig;
  zeroCommissionIncludedDesignerIds: Array<Id>;
};

export const emptyPublicConfig: PublicConfig = {
  algolia: emptyAlgoliaConfig,
  appleSignInClientId: "",
  appleSignInRedirectUri: "",
  badges: [],
  appsflyerSmartBannerKey: null,
  branchKeyLive: null,
  categories: {},
  colors: { primaryColors: [] },
  contentful: { host: "contentful-preview.grailed.com", spaces: {} },
  countries: [],
  env: "development",
  facebookAppId: 0,
  googleAnalytics: { trackingId: "", domain: "" },
  googleOauthClientId: "",
  inBeta: false,
  internationalTaxable: [],
  listingMaxPrice: 0,
  listingMinPrice: 0,
  listingGpfsMinPrice: 0,
  locations: [],
  measurementTypes: [],
  recaptcha: {},
  userRefundReasons: [],
  requiresPostalCode: [],
  sellformSuggestedDesignerIds: [],
  sellerBadges: [],
  sizes: {},
  stratas: [],
  segmentWriteKey: "",
  stripePublishableKey: "",
  styles: [],
  trustOptions: { scores: [], reasons: [] },
  unbrandedDesignerId: 0,
  urls: {},
  vacationModeLimit: 0,
  bundlesDomain: "",
  riskifiedShopDomain: "",
  oneTrustDomainScript: "",
  sentry: emptySentryConfig,
  zeroCommissionIncludedDesignerIds: [],
};

export const fromWindow: (arg0: typeof window) => PublicConfig = memoize(
  ({ PUBLIC_CONFIG }) => camelize(PUBLIC_CONFIG),
);

export const getStratasFromConfig = (config: PublicConfig): Array<Strata> =>
  config.stratas;

export const getBadgesFromConfig = (config: PublicConfig): Array<Badge> =>
  config.badges;

export const getRequiresPostalCodeFromConfig = (
  config: PublicConfig,
): Array<string> => config.requiresPostalCode;

export const getContentfulConfigs = (
  config: PublicConfig,
): ContentfulConfig => ({
  host: config.contentful.host,
  accessToken: config.contentful.spaces.grailed.accessToken,
  spaceId: config.contentful.spaces.grailed.spaceId,
});

export const getCountriesFromConfig = (config: PublicConfig): Array<Country> =>
  config.countries;

export const getSellerBadges = (config: PublicConfig): Array<SellerBadge> =>
  config.sellerBadges;

export const getTaxableCountries = (config: PublicConfig): Array<Country> =>
  config.internationalTaxable;
